import { LoaderFunction } from 'react-router-dom';
import { ContentType } from '@atlassian/learning-components';

import { fetchGraphQL } from 'src/services/contentful/client';
import { ContentfulSmCourse, ContentfulSmCourseResponse, GET_SM_COURSE } from 'src/graphql/queries/GetSmCourse';
import { ContentfulSmLesson, ContentfulSmLessonResponse, GET_SM_LESSON } from 'src/graphql/queries/GetSmLesson';
import { GetCourseQueryVariables } from 'src/graphql/generated/GetSmCourse';
import { GetSmLessonQueryVariables } from 'src/graphql/generated/GetSmLesson';
import { GetLearningPathWithNavigationQueryVariables } from 'src/graphql/generated/graphql';
import {
  ContentfulLearningPathWithNavigation,
  ContentfulLearningPathWithNavigationResponse,
  GET_LEARNING_PATH_WITH_NAVIGATION,
} from 'src/graphql/queries/GetLearningPathWithNavigation';
import { getNotificationsById, NotificationsMappedResponse } from 'src/services/http/notifications';
import { mapLessonNotificationsData, sortNotificationsByDate } from 'src/utils/transformNotificationData';
import { isPreviewMode } from 'src/utils/isPreviewMode';

export const loader: LoaderFunction = async ({ params }): Promise<LessonLoaderProps> => {
  const lessonId = params.lessonId ?? '';
  const courseId = params.courseId ?? '';
  const pathId = params.pathId ?? '';

  const preview = isPreviewMode();

  let lesson: ContentfulSmLesson | undefined;
  let course: ContentfulSmCourse | undefined;
  let learningPath: ContentfulLearningPathWithNavigation | undefined;

  if (lessonId) {
    lesson = await getLessonData(lessonId, preview);

    if (!lesson) {
      throw new Error('Lesson was not found');
    }
  }

  if (courseId) {
    course = await getCourse(courseId, preview);

    if (!course) {
      throw new Error('Course was not found');
    }
  }

  if (pathId) {
    learningPath = await getLearningPath(pathId, preview);

    if (!learningPath) {
      throw new Error('Learning path was not found');
    }
  }

  const lessonNotifications = await getNotificationsById(lesson!.sys.id, ContentType.LESSON);

  const sortedUnseenNotifications = sortNotificationsByDate(lessonNotifications?.unseenNotifications);
  const sortedHistoricalNotifications = sortNotificationsByDate(lessonNotifications?.historicalNotifications);
  const mappedUnseenNotifications = mapLessonNotificationsData(sortedUnseenNotifications);
  const mappedHistoricalNotifications = mapLessonNotificationsData(sortedHistoricalNotifications);

  return {
    lesson: lesson!,
    course,
    learningPath,
    notifications: {
      unseenNotifications: mappedUnseenNotifications,
      historicalNotifications: mappedHistoricalNotifications,
    },
  };
};

const getLessonData = async (lessonId: string, preview: boolean): Promise<ContentfulSmLesson | undefined> => {
  const response = await fetchGraphQL<ContentfulSmLessonResponse, GetSmLessonQueryVariables>(GET_SM_LESSON, {
    slug: lessonId,
    preview,
  });

  return response.smLessonCollection.items[0];
};

const getCourse = async (courseId: string, preview: boolean): Promise<ContentfulSmCourse | undefined> => {
  const response = await fetchGraphQL<ContentfulSmCourseResponse, GetCourseQueryVariables>(GET_SM_COURSE, {
    slug: courseId,
    preview,
  });

  return response.smCourseCollection.items[0];
};

const getLearningPath = async (
  pathId: string,
  preview: boolean,
): Promise<ContentfulLearningPathWithNavigation | undefined> => {
  const response = await fetchGraphQL<
    ContentfulLearningPathWithNavigationResponse,
    GetLearningPathWithNavigationQueryVariables
  >(GET_LEARNING_PATH_WITH_NAVIGATION, {
    slug: pathId,
    preview,
  });

  return response.learningPathCollection.items[0];
};

export interface LessonLoaderProps {
  lesson: ContentfulSmLesson;
  course?: ContentfulSmCourse;
  learningPath?: ContentfulLearningPathWithNavigation;
  notifications: NotificationsMappedResponse;
}
